import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider } from './context/BrandingContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Servers from './pages/assets/Servers';
import ServerDetail from './pages/assets/ServerDetail';
import Certificates from './pages/assets/Certificates';
import Switches from './pages/assets/Switches';
import SwitchDetail from './pages/assets/SwitchDetail';
import Enlaces from './pages/assets/Enlaces';
import Firewalls from './pages/assets/Firewalls';
import SqlServers from './pages/assets/SqlServers';
import SqlServerDetail from './pages/assets/SqlServerDetail';
import Usuarios from './pages/config/Usuarios';
import Empresas from './pages/config/Empresas';
import Clientes from './pages/config/Clientes';
import Sites from './pages/config/Sites';
import Ubicaciones from './pages/config/Ubicaciones';
import Estados from './pages/config/Estados';
import CatalogPage from './pages/config/CatalogPage';
import ProveedoresPage from './pages/config/ProveedoresPage';
import ModelosAPPage from './pages/config/ModelosAPPage';
import DVRsPage from './pages/config/DVRsPage';
import KBCatalogPage from './pages/config/KBCatalogPage';
import BackupPoliciesPage from './pages/config/BackupPoliciesPage';
import BackupToolsPage from './pages/config/BackupToolsPage';
import AgentesPage from './pages/config/AgentesPage';
import ModelosUPSPage from './pages/config/ModelosUPSPage';
import TiposLicenciaPage from './pages/config/TiposLicenciaPage';
import TiposVencimientoPage from './pages/config/TiposVencimientoPage';
import TiemposAlertaPage from './pages/config/TiemposAlertaPage';
import TiposIoTPage from './pages/config/TiposIoTPage';
import TiposStoragePage from './pages/config/TiposStoragePage';
import TiposPrinterPage from './pages/config/TiposPrinterPage';
import BrandingPage from './pages/config/BrandingPage';
import Tenants from './pages/assets/Tenants';
import Licencias from './pages/assets/Licencias';
import IoT from './pages/assets/IoT';
import Storage from './pages/assets/Storage';
import DNS from './pages/assets/DNS';
import DNSDetail from './pages/assets/DNSDetail';
import Printers from './pages/assets/Printers';
import PrinterDetail from './pages/assets/PrinterDetail';
import AccessPoints from './pages/assets/AccessPoints';
import Ups from './pages/assets/Ups';
import Camaras from './pages/assets/Camaras';
import Workstations from './pages/assets/Workstations';
import WorkstationDetail from './pages/assets/WorkstationDetail';
import Tablets from './pages/assets/Tablets';
import TabletDetail from './pages/assets/TabletDetail';
import MobileDevices from './pages/assets/MobileDevices';
import MobileDeviceDetail from './pages/assets/MobileDeviceDetail';
import Proyectores from './pages/assets/Proyectores';
import ProyectorDetail from './pages/assets/ProyectorDetail';
import Televisores from './pages/assets/Televisores';
import TelevisorDetail from './pages/assets/TelevisorDetail';
import TelefonosIP from './pages/assets/TelefonosIP';
import TelefonoIPDetail from './pages/assets/TelefonoIPDetail';
import Relojes from './pages/assets/Relojes';
import RelojDetail from './pages/assets/RelojDetail';
import Monitores from './pages/assets/Monitores';
import Applications from './pages/assets/Applications';
import ApplicationDetail from './pages/assets/ApplicationDetail';
import ApplicationMap from './pages/assets/ApplicationMap';
import AppInterfaces from './pages/apps/AppInterfaces';
import UsersPage from './pages/admin/Users';
import RolesPage from './pages/admin/Roles';
import IbiConfiguration from './pages/admin/IbiConfiguration';

// IPAM Pages
import IPAMOverview from './pages/ipam/IPAMOverview';
import L2DomainsPage from './pages/ipam/L2DomainsPage';
import L2DomainDetail from './pages/ipam/L2DomainDetail';
import VLANsPage from './pages/ipam/VLANsPage';
import VLANDetail from './pages/ipam/VLANDetail';
import SubnetsPage from './pages/ipam/SubnetsPage';
import SubnetDetailVisual from './pages/ipam/SubnetDetailVisual';
import AddressesPage from './pages/ipam/AddressesPage';

// Reports Pages
import InventoryReport from './pages/reports/InventoryReport';
import OSReport from './pages/reports/OSReport';
import SoftwareReport from './pages/reports/SoftwareReport';
import DiscoveryReport from './pages/reports/DiscoveryReport';
import PatchesReport from './pages/reports/PatchesReport';
import ExecutiveReport from './pages/reports/ExecutiveReport';
import ComplianceWorkstations from './pages/reports/ComplianceWorkstations';
import TraceabilityReport from './pages/reports/TraceabilityReport';
import PendingServersReport from './pages/reports/PendingServersReport';
import BackupCoverageReport from './pages/reports/BackupCoverageReport';
import SqlNoMaxMemoryReport from './pages/reports/SqlNoMaxMemoryReport';
import SqlTraceabilityReport from './pages/reports/SqlTraceabilityReport';
import DnsTraceabilityReport from './pages/reports/DnsTraceabilityReport';
import DnsSecurityChecks from './pages/reports/DnsSecurityChecks';
import VirtSnapshotsReport from './pages/reports/VirtSnapshotsReport';
import VirtToolsReport from './pages/reports/VirtToolsReport';
import HypervCheckpointsReport from './pages/reports/HypervCheckpointsReport';
import HypervIntegrationReport from './pages/reports/HypervIntegrationReport';

// Virtualization
import VirtPlatforms from './pages/infra/virt/VirtPlatforms';
import VirtVMs from './pages/infra/virt/VirtVMs';
import VirtDatastores from './pages/infra/virt/VirtDatastores';

// Hyper-V
import HypervHosts from './pages/infra/hyperv/HypervHosts';
import HypervVMs from './pages/infra/hyperv/HypervVMs';
import HypervStorage from './pages/infra/hyperv/HypervStorage';
import HypervVhds from './pages/infra/hyperv/HypervVhds';

// Protected Route component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Placeholder for pages not yet implemented
const ComingSoon = ({ title }) => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-3xl font-bold mb-2">{title}</h1>
      <p className="text-muted-foreground">Próximamente...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      
      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      
      {/* Admin routes */}
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><UsersPage /></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute requireAdmin><RolesPage /></ProtectedRoute>} />
      <Route path="/admin/ibi" element={<ProtectedRoute requireAdmin><IbiConfiguration /></ProtectedRoute>} />
      
      {/* Assets */}
      <Route path="/assets/servers" element={<ProtectedRoute><Servers /></ProtectedRoute>} />
      <Route path="/assets/servers/:hostname" element={<ProtectedRoute><ServerDetail /></ProtectedRoute>} />
      <Route path="/assets/switches" element={<ProtectedRoute><Switches /></ProtectedRoute>} />
      <Route path="/assets/switches/:id" element={<ProtectedRoute><SwitchDetail /></ProtectedRoute>} />
      <Route path="/assets/firewalls" element={<ProtectedRoute><Firewalls /></ProtectedRoute>} />
      <Route path="/assets/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
      <Route path="/assets/links" element={<ProtectedRoute><Enlaces /></ProtectedRoute>} />
      <Route path="/assets/sql" element={<ProtectedRoute><SqlServers /></ProtectedRoute>} />
      <Route path="/assets/sql/:id" element={<ProtectedRoute><SqlServerDetail /></ProtectedRoute>} />
      <Route path="/assets/licenses" element={<ProtectedRoute><Licencias /></ProtectedRoute>} />
      <Route path="/assets/nas" element={<ProtectedRoute><Storage /></ProtectedRoute>} />
      <Route path="/assets/access-points" element={<ProtectedRoute><AccessPoints /></ProtectedRoute>} />
      <Route path="/assets/cameras" element={<ProtectedRoute><Camaras /></ProtectedRoute>} />
      <Route path="/assets/iot" element={<ProtectedRoute><IoT /></ProtectedRoute>} />
      <Route path="/assets/tenants" element={<ProtectedRoute><Tenants /></ProtectedRoute>} />
      <Route path="/assets/ups" element={<ProtectedRoute><Ups /></ProtectedRoute>} />
      <Route path="/micro/workstations" element={<ProtectedRoute><Workstations /></ProtectedRoute>} />
      <Route path="/micro/workstations/:id" element={<ProtectedRoute><WorkstationDetail /></ProtectedRoute>} />
      <Route path="/assets/dns" element={<ProtectedRoute><DNS /></ProtectedRoute>} />
      <Route path="/assets/dns/:id" element={<ProtectedRoute><DNSDetail /></ProtectedRoute>} />
      
      {/* Microinformática */}
      <Route path="/micro/mobile-devices" element={<ProtectedRoute><MobileDevices /></ProtectedRoute>} />
      <Route path="/micro/mobile-devices/:id" element={<ProtectedRoute><MobileDeviceDetail /></ProtectedRoute>} />
      <Route path="/micro/proyectores" element={<ProtectedRoute><Proyectores /></ProtectedRoute>} />
      <Route path="/micro/proyectores/:id" element={<ProtectedRoute><ProyectorDetail /></ProtectedRoute>} />
      <Route path="/micro/cellphones" element={<ProtectedRoute><ComingSoon title="Celulares" /></ProtectedRoute>} />
      <Route path="/micro/computers" element={<ProtectedRoute><ComingSoon title="Computadoras" /></ProtectedRoute>} />
      <Route path="/micro/printers" element={<ProtectedRoute><Printers /></ProtectedRoute>} />
      <Route path="/micro/printers/:id" element={<ProtectedRoute><PrinterDetail /></ProtectedRoute>} />
      <Route path="/micro/projectors" element={<ProtectedRoute><ComingSoon title="Proyectores" /></ProtectedRoute>} />
      <Route path="/micro/clocks" element={<ProtectedRoute><Relojes /></ProtectedRoute>} />
      <Route path="/micro/clocks/:id" element={<ProtectedRoute><RelojDetail /></ProtectedRoute>} />
      <Route path="/micro/tvs" element={<ProtectedRoute><Televisores /></ProtectedRoute>} />
      <Route path="/micro/tvs/:id" element={<ProtectedRoute><TelevisorDetail /></ProtectedRoute>} />
      <Route path="/micro/ip-phones" element={<ProtectedRoute><TelefonosIP /></ProtectedRoute>} />
      <Route path="/micro/ip-phones/:id" element={<ProtectedRoute><TelefonoIPDetail /></ProtectedRoute>} />
      <Route path="/micro/tablets" element={<ProtectedRoute><Tablets /></ProtectedRoute>} />
      <Route path="/micro/tablets/:id" element={<ProtectedRoute><TabletDetail /></ProtectedRoute>} />
      <Route path="/micro/monitores" element={<ProtectedRoute><Monitores /></ProtectedRoute>} />
      <Route path="/micro/ip-phones" element={<ProtectedRoute><ComingSoon title="Teléfonos IP" /></ProtectedRoute>} />
      
      {/* Applications - Mapa de Aplicaciones (nueva sección /apps) */}
      <Route path="/apps" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
      <Route path="/apps/map" element={<ProtectedRoute><ApplicationMap /></ProtectedRoute>} />
      <Route path="/apps/interfaces" element={<ProtectedRoute><AppInterfaces /></ProtectedRoute>} />
      <Route path="/apps/:id" element={<ProtectedRoute><ApplicationDetail /></ProtectedRoute>} />
      
      {/* Legacy routes redirect */}
      <Route path="/assets/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
      <Route path="/assets/applications/map" element={<ProtectedRoute><ApplicationMap /></ProtectedRoute>} />
      <Route path="/assets/applications/:id" element={<ProtectedRoute><ApplicationDetail /></ProtectedRoute>} />
      
      {/* Config - Organización (páginas existentes con rutas actualizadas) */}
      <Route path="/config/usuarios" element={<ProtectedRoute><Usuarios /></ProtectedRoute>} />
      <Route path="/config/empresas" element={<ProtectedRoute><Empresas /></ProtectedRoute>} />
      <Route path="/config/clientes" element={<ProtectedRoute><Clientes /></ProtectedRoute>} />
      <Route path="/config/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
      <Route path="/config/ubicaciones" element={<ProtectedRoute><Ubicaciones /></ProtectedRoute>} />
      <Route path="/config/estados" element={<ProtectedRoute><Estados /></ProtectedRoute>} />
      
      {/* Config - Catálogos genéricos (ruta dinámica) */}
      <Route path="/config/criticidad" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/tipos-dispositivo" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/tipos-vencimiento" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/ambientes" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/cloud" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/sistemas-operativos" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/ventanas-reinicio" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/tipos-conexion" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/tipos-enlace" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/roles-enlace" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/modelos-firewalls" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/marcas" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/modelos-switches" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      
      {/* Config - Terceros (páginas específicas) */}
      <Route path="/config/proveedores" element={<ProtectedRoute><ProveedoresPage /></ProtectedRoute>} />
      <Route path="/config/modelos-ap" element={<ProtectedRoute><ModelosAPPage /></ProtectedRoute>} />
      <Route path="/config/dvrs" element={<ProtectedRoute><DVRsPage /></ProtectedRoute>} />
      <Route path="/config/modelos-ups" element={<ProtectedRoute><ModelosUPSPage /></ProtectedRoute>} />
      <Route path="/config/tipos-licencia" element={<ProtectedRoute><TiposLicenciaPage /></ProtectedRoute>} />
      <Route path="/config/tipos-vencimiento-licencias" element={<ProtectedRoute><TiposVencimientoPage /></ProtectedRoute>} />
      <Route path="/config/tiempos-alerta" element={<ProtectedRoute><TiemposAlertaPage /></ProtectedRoute>} />
      <Route path="/config/tipos-iot" element={<ProtectedRoute><TiposIoTPage /></ProtectedRoute>} />
      <Route path="/config/tipos-storage" element={<ProtectedRoute><TiposStoragePage /></ProtectedRoute>} />
      <Route path="/config/tipos-printer" element={<ProtectedRoute><TiposPrinterPage /></ProtectedRoute>} />
      
      {/* Config - Sistema */}
      <Route path="/config/kb-catalog" element={<ProtectedRoute><KBCatalogPage /></ProtectedRoute>} />
      <Route path="/config/backup-policies" element={<ProtectedRoute><BackupPoliciesPage /></ProtectedRoute>} />
      <Route path="/config/backup-tools" element={<ProtectedRoute><BackupToolsPage /></ProtectedRoute>} />
      <Route path="/config/agentes" element={<ProtectedRoute><AgentesPage /></ProtectedRoute>} />
      
      {/* Config - Apps (catálogos del módulo Aplicaciones) */}
      <Route path="/config/app-types" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/app-status" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/app-roles" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/app-interface-types" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/config/app-db-types" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      
      {/* Config - Tema */}
      <Route path="/config/tema" element={<ProtectedRoute><BrandingPage /></ProtectedRoute>} />
      
      {/* IPAM */}
      <Route path="/ipam" element={<ProtectedRoute><IPAMOverview /></ProtectedRoute>} />
      <Route path="/ipam/l2-domains" element={<ProtectedRoute><L2DomainsPage /></ProtectedRoute>} />
      <Route path="/ipam/l2-domains/:id" element={<ProtectedRoute><L2DomainDetail /></ProtectedRoute>} />
      <Route path="/ipam/vlans" element={<ProtectedRoute><VLANsPage /></ProtectedRoute>} />
      <Route path="/ipam/vlans/:id" element={<ProtectedRoute><VLANDetail /></ProtectedRoute>} />
      <Route path="/ipam/subnets" element={<ProtectedRoute><SubnetsPage /></ProtectedRoute>} />
      <Route path="/ipam/subnets/:id" element={<ProtectedRoute><SubnetDetailVisual /></ProtectedRoute>} />
      <Route path="/ipam/addresses" element={<ProtectedRoute><AddressesPage /></ProtectedRoute>} />
      
      {/* Reports */}
      <Route path="/reports/inventory" element={<ProtectedRoute><InventoryReport /></ProtectedRoute>} />
      <Route path="/reports/os" element={<ProtectedRoute><OSReport /></ProtectedRoute>} />
      <Route path="/reports/software" element={<ProtectedRoute><SoftwareReport /></ProtectedRoute>} />
      <Route path="/reports/network-changes" element={<ProtectedRoute><DiscoveryReport /></ProtectedRoute>} />
      <Route path="/reports/patches" element={<ProtectedRoute><PatchesReport /></ProtectedRoute>} />
      <Route path="/reports/compliance-servers" element={<ProtectedRoute><ExecutiveReport /></ProtectedRoute>} />
      <Route path="/reports/compliance-workstations" element={<ProtectedRoute><ComplianceWorkstations /></ProtectedRoute>} />
      <Route path="/reports/traceability" element={<ProtectedRoute><TraceabilityReport /></ProtectedRoute>} />
      <Route path="/reports/pending-servers" element={<ProtectedRoute><PendingServersReport /></ProtectedRoute>} />
      <Route path="/reports/backup-coverage" element={<ProtectedRoute><BackupCoverageReport /></ProtectedRoute>} />
      <Route path="/reports/sql/no-maxmemory" element={<ProtectedRoute><SqlNoMaxMemoryReport /></ProtectedRoute>} />
      <Route path="/reports/sql/traceability" element={<ProtectedRoute><SqlTraceabilityReport /></ProtectedRoute>} />
      <Route path="/reports/dns/traceability" element={<ProtectedRoute><DnsTraceabilityReport /></ProtectedRoute>} />
      <Route path="/reports/dns/security" element={<ProtectedRoute><DnsSecurityChecks /></ProtectedRoute>} />
      <Route path="/reports/virt/snapshots" element={<ProtectedRoute><VirtSnapshotsReport /></ProtectedRoute>} />
      <Route path="/reports/virt/tools" element={<ProtectedRoute><VirtToolsReport /></ProtectedRoute>} />
      <Route path="/reports/virt/hyperv/checkpoints" element={<ProtectedRoute><HypervCheckpointsReport /></ProtectedRoute>} />
      <Route path="/reports/virt/hyperv/tools" element={<ProtectedRoute><HypervIntegrationReport /></ProtectedRoute>} />
      
      {/* Virtualization */}
      <Route path="/infra/virt/platforms" element={<ProtectedRoute><VirtPlatforms /></ProtectedRoute>} />
      <Route path="/infra/virt/vms" element={<ProtectedRoute><VirtVMs /></ProtectedRoute>} />
      <Route path="/infra/virt/datastores" element={<ProtectedRoute><VirtDatastores /></ProtectedRoute>} />

      {/* Hyper-V */}
      <Route path="/infra/hyperv/hosts" element={<ProtectedRoute><HypervHosts /></ProtectedRoute>} />
      <Route path="/infra/hyperv/vms" element={<ProtectedRoute><HypervVMs /></ProtectedRoute>} />
      <Route path="/infra/hyperv/storage" element={<ProtectedRoute><HypervStorage /></ProtectedRoute>} />
      <Route path="/infra/hyperv/vhds" element={<ProtectedRoute><HypervVhds /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <ThemeProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <Toaster position="top-right" offset="60px" richColors />
          </ThemeProvider>
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
